import type { TypedDocumentNode, VariablesOf } from '@graphql-typed-document-node/core';
import { useMutation, UseMutationOptions, useQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { print } from 'graphql';
import { CaseStyle, DateTimePrecision } from '@mews/enums';
import { sendRequest } from '@mews/framework';
import { CommonRepositoryParams } from '../createRegistry';
import { useGraphqlClientConfig } from './graphqlClientConfigContext';

interface GraphQLQueryOptions<TResult, TVariables> {
    document: TypedDocumentNode<TResult, TVariables>;
    variables?: TVariables extends VariablesOf<TypedDocumentNode<TResult, TVariables>> ? TVariables : never;
    signal?: AbortSignal;
}

export const sendGraphQLRequest = async <TResult, TVariables>(
    url: string,
    graphQLQueryOptions: GraphQLQueryOptions<TResult, TVariables>,
    commonParams: CommonRepositoryParams
): Promise<TResult> => {
    const body = {
        query: print(graphQLQueryOptions.document),
        variables: graphQLQueryOptions.variables,
    };

    return await sendRequest(
        url,
        {
            customHeaders: {
                'X-Accept-Casing': CaseStyle.Camel,
                'X-Accept-DateTime-Precision': DateTimePrecision.Milliseconds,
                'X-Mews-EnterpriseId': commonParams.enterpriseId,
            },
            credentials: 'include',
            accept: 'application/graphql-response+json',
            signal: graphQLQueryOptions.signal,
        },
        body
    ).then((response) => response.data as TResult);
};

export const useGraphqlQuery = <TResult, TVariables>(
    url: string,
    graphQLQueryOptions: GraphQLQueryOptions<TResult, TVariables>,
    queryOptions?: Omit<UseQueryOptions<TResult>, 'queryKey' | 'queryFn'>
): UseQueryResult<TResult> => {
    const { commonRepositoryParams } = useGraphqlClientConfig();
    const commonParams = commonRepositoryParams();

    return useQuery({
        queryKey: [url, graphQLQueryOptions, commonParams],
        queryFn: async ({ signal }) =>
            await sendGraphQLRequest(
                url,
                { ...graphQLQueryOptions, signal: graphQLQueryOptions.signal || signal },
                commonParams
            ),
        ...queryOptions,
    });
};

export const useGraphqlMutation = <TResult, TVariables>(
    url: string,
    graphQLQueryOptions: GraphQLQueryOptions<TResult, TVariables>,
    mutationOptions?: Omit<UseMutationOptions<TResult, Error, TVariables>, 'mutationKey' | 'mutationFn'>
) => {
    const { onSuccess, ...options } = mutationOptions ?? {};
    const { commonRepositoryParams } = useGraphqlClientConfig();
    const commonParams = commonRepositoryParams();

    return useMutation({
        mutationKey: [url, graphQLQueryOptions],
        mutationFn: async (params) =>
            await sendGraphQLRequest(
                url,
                {
                    document: graphQLQueryOptions.document,
                    variables: { ...graphQLQueryOptions.variables, ...params },
                },
                commonParams
            ),
        onSuccess: (...args) => {
            onSuccess?.(...args);
        },
        ...options,
    });
};
